// alerts.js
// Single source of truth for the alerts system:
//   - Alert type catalog (defaults: character, color, position)
//   - Default message templates (overridable via config/alertTemplates)
//   - Test sample data for the Stream Setup harness
//   - Scene definitions for the per-scene Preview buttons in StreamSetup
//   - writeAlert() helper used by both the test harness and (slice 3) the
//     real GTS event handlers
//
// IMPORTANT: when slice 3 wires real triggers, those handlers should call
// writeAlert(type, fields) and let this file own the message rendering.

import { addDoc, collection, serverTimestamp, Timestamp } from "firebase/firestore";
import { db } from "./../firebase";

// ── ALERT TYPE CATALOG ───────────────────────────────────────────────────────
// Character keys reference flash designs (config/flashDesigns) when they exist;
// CharacterBadge falls back to a styled SVG initial badge for keys that aren't
// yet illustrated (jacques, poffridge are coming from Wella).
//
// Position controls where the toast appears on the alerts overlay canvas.
// All defaults use the LEFT side because Main's overlay puts the queue card
// panel and artist cam frame on the right — alerts on the right would cover
// existing UI. Top positions also clear Main's top blue bar (~88px tall).
// Color is a CSS variable from the brand palette (set in index.css).
export const ALERT_DEFAULTS = {
  new_order:           { character: "poffridge",      position: "top-left",    color: "var(--jop-blue)"            },
  queue_milestone:     { character: "charli",         position: "top-left",    color: "var(--jop-orange)"          },
  queue_full:          { character: "jacques",        position: "top-left",    color: "var(--jop-pink)"            },
  queue_open:          { character: "charli",         position: "top-left",    color: "var(--jop-highlight-blue)"  },
  pancake_done:        { character: "poffridge",      position: "top-left",    color: "var(--jop-gold)"            },
  new_sub:             { character: "saturnina",      position: "bottom-left", color: "var(--jop-gold)"            },
  new_follow:          { character: "sunnySugar",     position: "bottom-left", color: "var(--jop-pink)"            },
  tip_received:        { character: "robert",         position: "bottom-left", color: "var(--jop-gold)"            },
  custom_announcement: { character: "hermiethewurmy", position: "top-left",    color: "var(--jop-orange)"          },
  shift_started:       { character: "charli",         position: "top-left",    color: "var(--jop-highlight-blue)"  },
  shift_ended:         { character: "saturnina",      position: "top-left",    color: "var(--jop-pink)"            },
};

export const ALERT_TYPES = Object.keys(ALERT_DEFAULTS);

// ── MESSAGE TEMPLATES ────────────────────────────────────────────────────────
// Stored in config/alertTemplates and editable from Stream Setup. These are
// the seed values used until staff overrides them. Variables in {curlies} are
// substituted from the alert doc's fields by renderAlertMessage().
export const DEFAULT_ALERT_TEMPLATES = {
  new_order:           "🎟️ New ticket! {name} wants a {subject}",
  queue_milestone:     "🔥 {count} pancakes in the queue!",
  queue_full:          "🚫 Queue is full — we're cookin'!",
  queue_open:          "✅ Queue is open! Grab your Golden Ticket!",
  pancake_done:        "✨ {name}'s pancake is done! Beautiful.",
  new_sub:             "🌟 {name} just subscribed! Thank you!",
  new_follow:          "👋 {name} just followed!",
  tip_received:        "💛 {name} left a tip! You're amazing.",
  custom_announcement: "{message}",
  shift_started:       "🥞 We're live! Today's shift is open.",
  shift_ended:         "👋 That's a wrap — thanks for watching!",
};

// Friendly labels for the test harness UI
export const ALERT_LABELS = {
  new_order:           "New Order",
  queue_milestone:     "Queue Milestone",
  queue_full:          "Queue Full",
  queue_open:          "Queue Open",
  pancake_done:        "Pancake Done",
  new_sub:             "New Sub",
  new_follow:          "New Follow",
  tip_received:        "Tip Received",
  custom_announcement: "Announcement",
  shift_started:       "Shift Started",
  shift_ended:         "Shift Ended",
};

// ── TEMPLATE RENDERER ────────────────────────────────────────────────────────
// Replaces {placeholders} in a template string with values from `fields`.
// Falls back to the placeholder unchanged if the value is missing.
export function renderAlertMessage(template, fields = {}) {
  if (!template) return "";
  return template.replace(/\{(\w+)\}/g, (match, key) =>
    fields[key] != null ? String(fields[key]) : match
  );
}

// ── TEST SAMPLE DATA ─────────────────────────────────────────────────────────
// Sample fields used by the Test Alerts buttons in Stream Setup. Each test
// alert uses these to fill in template variables, plus the matching default
// character/color/position from ALERT_DEFAULTS.
export const TEST_ALERT_SAMPLES = {
  new_order:           { name: "Sarah",  subject: "an otter" },
  queue_milestone:     { count: 10 },
  queue_full:          {},
  queue_open:          {},
  pancake_done:        { name: "Marcus" },
  new_sub:             { name: "Quinn" },
  new_follow:          { name: "Jamie" },
  tip_received:        { name: "Riley" },
  custom_announcement: { message: "🎉 Special order — give it up for Bree!" },
  shift_started:       {},
  shift_ended:         {},
};

// ── SCENE DEFINITIONS ────────────────────────────────────────────────────────
// Drives the Per-Scene panel in StreamSetup (Preview + Copy URL buttons).
// `built` flips to true as we ship each scene in slices 4-12.
export const OVERLAY_SCENES = [
  { id: "main",           label: "Main (calm)",         path: "/overlay/main",          built: true,  description: "Calm show scene for brunch/party events — no flash board, marquee or promos. Queue cards, flip countdown, tip jar (market voice only)." },
  { id: "alerts",         label: "Alerts (always-on)",  path: "/overlay/alerts",        built: true,  description: "Toast layer added on top of every other scene in OBS." },
  { id: "starting-soon",  label: "Starting Soon",       path: "/overlay/starting-soon", built: true,  description: "Pre-stream holding screen with live queue building." },
  { id: "face-cam",       label: "Face Cam",            path: "/overlay/face-cam",      built: true,  description: "Minimal watermark + queue counter for talking-head scenes." },
  { id: "reference",      label: "Reference",           path: "/overlay/reference",     built: true,  description: "Reference image for the active drawing order." },
  { id: "screenshare",    label: "Screen Share",        path: "/overlay/screenshare",   built: true,  description: "Persistent lower-third bar for screen-share scenes." },
  { id: "break",          label: "On Break",            path: "/overlay/break",         built: true,  description: "Transparent overlay for break video — marquee columns + be-right-back banner." },
  { id: "outro",          label: "Outro",               path: "/overlay/outro",         built: true,  description: "Closing credits with tonight's pancakes gallery, socials, tip jar." },
  { id: "booth",          label: "Booth Menu Board",    path: "/overlay/booth",         built: true,  description: "In-person booth display: flash designs left, transparent griddle hole center, rotating promos right, live status row along bottom." },
];

// ── WRITE HELPER ─────────────────────────────────────────────────────────────
// Creates a new alert doc with sensible defaults. `extras` can override any
// field on the doc (character, position, color, message override, ttl).
export async function writeAlert(type, fields = {}, extras = {}) {
  const def = ALERT_DEFAULTS[type];
  if (!def) throw new Error(`Unknown alert type: ${type}`);
  const ttlSec = extras.ttlSec ?? 15;
  const expiresAt = Timestamp.fromMillis(Date.now() + ttlSec * 1000);
  const doc = {
    type,
    character:  extras.character ?? def.character,
    position:   extras.position  ?? def.position,
    color:      extras.color     ?? def.color,
    fields,
    // If `extras.message` is provided, the overlay uses it as-is and skips
    // template rendering. Otherwise the overlay looks up the template from
    // config/alertTemplates (or DEFAULT_ALERT_TEMPLATES) and renders it with
    // the `fields` object.
    message:    extras.message   ?? null,
    createdAt:  serverTimestamp(),
    expiresAt,
    consumed:   false,
  };
  return addDoc(collection(db, "alerts"), doc);
}
